import React, { useState } from 'react';
import SPAPill from '../shared/SPAPill';
import FilterPill from './FilterPill';
import { MetricCard, MetricCardSkeleton } from './Metric';
import ThrottledOverTime from './ThrottledOverTime';
import useRateLimits, { SubscriberPeak, SubscriberThrottled } from './useRateLimits';
import SPASectionHeading from '../shared/SPASectionHeading';

// No 30d — see LmsGradeSyncRateLimitService::WINDOWS.
const WINDOWS = ['24h', '7d'];

// Canvas tolerates ~100 calls/sec per district; a row past that is the one to open.
const CANVAS_CEILING = 100;

// A statement timeout returns a 503 naming the window to retry; that is worth showing verbatim.
const errorMessage = (error: unknown) => {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;

  return message ?? "Couldn't load rate limits.";
};

const SubscriberRow = ({ children, name, subscriberId }: {
  children: React.ReactNode;
  name: string | null;
  subscriberId: number;
}) => (
  <li
    className="tw-flex tw-items-baseline tw-gap-2 tw-border-0 tw-border-b tw-border-solid
      tw-border-spa-border tw-py-1.5 tw-text-sm"
  >
    <span className="tw-text-spa-primary">{name ?? `Subscriber ${subscriberId}`}</span>
    <span className="tw-ml-auto tw-tabular-nums tw-text-spa-secondary">{children}</span>
  </li>
);

const RateLimitsTab = () => {
  const [window, setWindow] = useState('24h');
  const { data, error } = useRateLimits(window);

  const totals = data?.totals;

  return (
    <>
      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {WINDOWS.map(value => (
          <FilterPill key={value} active={window === value} onClick={() => setWindow(value)}>
            {`Last ${value}`}
          </FilterPill>
        ))}
      </div>

      <p className="tw-mb-4 tw-mt-0 tw-max-w-3xl tw-text-sm tw-text-spa-secondary">
        Throttling is driven by how tightly calls arrive, not how many. A district sending 450 calls a
        minute in bursts gets rate limited while one sending more at a steady pace does not.
      </p>

      {error != null && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">{errorMessage(error)}</p>
      )}

      {data == null && error == null && (
        <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => <MetricCardSkeleton key={index} />)}
        </div>
      )}

      {totals != null && data != null && (
        <>
          <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3">
            <MetricCard
              hint="429s — the LMS refused the call"
              label="Rate limited"
              value={totals.throttled.toLocaleString()}
            />
            <MetricCard
              hint="Our limiter held the call back before sending"
              label="Deferred by us"
              value={totals.deferred.toLocaleString()}
            />
            <MetricCard
              hint="Not rate limiting — unlinked students, missing assignments, and similar"
              label="Other local refusals"
              value={totals.other_refusals.toLocaleString()}
            />
          </div>

          <SPASectionHeading title="Rate limited by vendor" />
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-gap-2">
            {Object.keys(data.throttled_by_vendor).length === 0 ? (
              <span className="tw-text-sm tw-text-spa-tertiary">No rate limiting in this window.</span>
            ) : (
              Object.entries(data.throttled_by_vendor).map(([vendor, count]) => (
                <SPAPill key={vendor} tone="error">
                  {`${vendor} · ${count.toLocaleString()}`}
                </SPAPill>
              ))
            )}
          </div>

          <SPASectionHeading className="tw-mb-1" title="Rate limiting over time" />
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            {window === '24h' ? 'Hourly.' : 'Daily.'}
            {' A single spike is usually a class change; a flat run of them is a loop.'}
          </p>
          <ThrottledOverTime bucketIsHour={window === '24h'} rows={data.throttled_over_time} />

          <SPASectionHeading className="tw-mb-1" title="Grade sync vs roster sync" />
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            Roster sync is what the assignee-refresh throttle controls. Shortening that window should
            raise roster calls and leave grade sync flat.
          </p>
          <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
            <MetricCard label="Grade sync calls" value={data.calls_by_sync_type.grade_sync.calls.toLocaleString()} />
            <MetricCard
              hint="Vendor rejections"
              label="Grade sync 429s"
              value={data.calls_by_sync_type.grade_sync.throttled.toLocaleString()}
            />
            <MetricCard label="Roster sync calls" value={data.calls_by_sync_type.roster_sync.calls.toLocaleString()} />
            <MetricCard
              hint="Vendor rejections"
              label="Roster sync 429s"
              value={data.calls_by_sync_type.roster_sync.throttled.toLocaleString()}
            />
          </div>

          <SPASectionHeading className="tw-mb-1" title="Peak calls per second" />
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            {`Always the last ${data.peak_per_second_window}, regardless of the window above — this one is `}
            too expensive to run over a longer period. Canvas throttles around
            {` ${CANVAS_CEILING}/sec per district; Schoology around 10–20/sec across all districts combined.`}
          </p>
          <ul className="tw-m-0 tw-mb-6 tw-list-none tw-p-0">
            {data.peak_per_second.length === 0 ? (
              <li className="tw-py-1.5 tw-text-sm tw-text-spa-tertiary">No LTI calls in the last 24h.</li>
            ) : (
              data.peak_per_second.map((row: SubscriberPeak) => (
                <SubscriberRow key={row.subscriber_id} name={row.name} subscriberId={row.subscriber_id}>
                  {`${row.peak_per_second.toLocaleString()}/sec`}
                </SubscriberRow>
              ))
            )}
          </ul>

          <SPASectionHeading className="tw-mb-1" title="Schoology, all districts combined" />
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            {`Every district shares one Schoology app and one budget, so only the combined rate reads
              here. Schoology publishes no remaining-budget header, so this rate against an observed
              ceiling of about ${data.schoology_global.ceiling}/sec is the only signal there is. Last 24h.`}
          </p>
          {data.schoology_global.calls === 0 ? (
            <p className="tw-mb-6 tw-mt-0 tw-text-sm tw-text-spa-secondary">
              No Schoology calls in the last 24h.
            </p>
          ) : (
            <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3">
              <MetricCard label="Calls" value={data.schoology_global.calls.toLocaleString()} />
              <MetricCard
                hint={`Ceiling is about ${data.schoology_global.ceiling}/sec`}
                label="Peak per second"
                value={data.schoology_global.peak_per_second.toLocaleString()}
              />
              <MetricCard
                hint="Where rejections come from"
                label="Seconds over ceiling"
                value={data.schoology_global.seconds_over_ceiling.toLocaleString()}
              />
            </div>
          )}

          <SPASectionHeading className="tw-mb-1" title="Canvas remaining budget" />
          <p className="tw-mb-2 tw-mt-0 tw-text-sm tw-text-spa-secondary">
            Canvas reports what is left of its budget on every response. Schoology reports nothing
            equivalent, so this covers Canvas only.
          </p>
          {data.remaining_budget.observed === 0 ? (
            <p className="tw-mb-6 tw-mt-0 tw-text-sm tw-text-spa-secondary">
              No readings in this window.
            </p>
          ) : (
            <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3">
              <MetricCard label="Readings" value={data.remaining_budget.observed.toLocaleString()} />
              <MetricCard hint="Closest to the ceiling" label="Lowest" value={data.remaining_budget.min} />
              <MetricCard label="Highest" value={data.remaining_budget.max} />
            </div>
          )}

          <SPASectionHeading title="Districts hitting rate limits" />
          <ul className="tw-m-0 tw-list-none tw-p-0">
            {data.subscribers.length === 0 ? (
              <li className="tw-py-1.5 tw-text-sm tw-text-spa-tertiary">None in this window.</li>
            ) : (
              data.subscribers.map((row: SubscriberThrottled) => (
                <SubscriberRow key={row.subscriber_id} name={row.name} subscriberId={row.subscriber_id}>
                  {`${row.throttled.toLocaleString()} rate limited`}
                </SubscriberRow>
              ))
            )}
          </ul>
        </>
      )}
    </>
  );
};

export default RateLimitsTab;
